import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import * as maptilersdk from '@maptiler/sdk'
import { circleCreateRequest } from '../../../../action/circle-actions'
import { collapseLeftToolbar } from '../../../../action/left-toolbar-state-actions'
import { MEASUREMENT_TYPES } from '../../../../lib/enums'
import toaster from '../../../main-ui/toast'

const CIRCLE_SRC = 'drawing-circle-src'
const CIRCLE_FILL = 'drawing-circle-fill'
const CIRCLE_LINE = 'drawing-circle-line'
const NUM_POINTS = 64

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function circleRing(lat, lng, radiusMeters) {
  const R = 6371000
  const d = radiusMeters / R
  const latR = lat * Math.PI / 180
  const lngR = lng * Math.PI / 180
  const coords = []
  for (let i = 0; i <= NUM_POINTS; i++) {
    const angle = (i * 2 * Math.PI) / NUM_POINTS
    const lat2 = Math.asin(Math.sin(latR) * Math.cos(d) + Math.cos(latR) * Math.sin(d) * Math.cos(angle))
    const lng2 = lngR + Math.atan2(Math.sin(angle) * Math.sin(d) * Math.cos(latR), Math.cos(d) - Math.sin(latR) * Math.sin(lat2))
    coords.push([lng2 * 180 / Math.PI, lat2 * 180 / Math.PI])
  }
  return coords
}

function formatRadius(meters) {
  if (meters >= 1000) return (meters / 1000).toFixed(2) + ' km'
  return Math.round(meters) + ' m'
}

const ThreeDCircleDetails = (props) => {
  const [color, setColor] = useState('#EA4335')
  const [name, setName] = useState(`Circle - ${props.circles.length + 1}`)
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [radiusDisplay, setRadiusDisplay] = useState('')

  const phase = useRef('idle') // 'idle' | 'radius'
  const centerRef = useRef(null)
  const radiusRef = useRef(0)
  const colorRef = useRef(color)
  const centerMarkerRef = useRef(null)

  useEffect(() => { colorRef.current = color }, [color])

  useEffect(() => {
    const map = props.mapReferenceThreed
    if (!map) return

    const addPreviewLayers = () => {
      if (map.getSource(CIRCLE_SRC)) return
      map.addSource(CIRCLE_SRC, {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[]] }, properties: {} }
      })
      map.addLayer({ id: CIRCLE_FILL, type: 'fill', source: CIRCLE_SRC, paint: { 'fill-color': colorRef.current, 'fill-opacity': 0.2 } })
      map.addLayer({ id: CIRCLE_LINE, type: 'line', source: CIRCLE_SRC, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': colorRef.current, 'line-width': 2, 'line-dasharray': [2, 1] } })
    }

    try { addPreviewLayers() } catch (_) {}
    map.on('styledata', addPreviewLayers)

    const handleClick = (e) => {
      const { lat, lng } = e.lngLat

      if (phase.current === 'idle') {
        centerRef.current = { lat, lng }
        phase.current = 'radius'

        const el = document.createElement('div')
        el.innerHTML = `<svg viewBox="0 0 12 12" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
          <circle cx="6" cy="6" r="5" fill="${colorRef.current}" stroke="white" stroke-width="1.5"/>
        </svg>`
        centerMarkerRef.current = new maptilersdk.Marker({
          element: el, anchor: 'center',
          pitchAlignment: 'viewport', rotationAlignment: 'viewport'
        }).setLngLat([lng, lat]).addTo(map)
      } else {
        // confirm radius — leave the preview on screen, ready to save
        const r = haversineMeters(centerRef.current.lat, centerRef.current.lng, lat, lng)
        radiusRef.current = Math.max(r, 10)
        setRadiusDisplay(formatRadius(radiusRef.current))
        phase.current = 'done'
      }
    }

    const handleMouseMove = (e) => {
      if (phase.current !== 'radius' || !centerRef.current) return
      const { lat, lng } = e.lngLat
      const r = Math.max(haversineMeters(centerRef.current.lat, centerRef.current.lng, lat, lng), 10)
      const src = map.getSource(CIRCLE_SRC)
      if (!src) return
      src.setData({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [circleRing(centerRef.current.lat, centerRef.current.lng, r)] },
        properties: {}
      })
      setRadiusDisplay(formatRadius(r))
    }

    map.on('click', handleClick)
    map.on('mousemove', handleMouseMove)
    map.getCanvas().style.cursor = 'crosshair'

    return () => {
      map.off('click', handleClick)
      map.off('mousemove', handleMouseMove)
      map.off('styledata', addPreviewLayers)
      map.getCanvas().style.cursor = ''
      try {
        if (map.getLayer(CIRCLE_FILL)) map.removeLayer(CIRCLE_FILL)
        if (map.getLayer(CIRCLE_LINE)) map.removeLayer(CIRCLE_LINE)
        if (map.getSource(CIRCLE_SRC)) map.removeSource(CIRCLE_SRC)
      } catch (_) {}
      if (centerMarkerRef.current) { centerMarkerRef.current.remove(); centerMarkerRef.current = null }
      phase.current = 'idle'
      centerRef.current = null
    }
  }, [props.mapReferenceThreed])

  const onSave = () => {
    if (!centerRef.current || radiusRef.current <= 0) return
    const { lat, lng } = centerRef.current
    const geoJson = JSON.stringify({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { radius: radiusRef.current, line: { color, fill: color } }
      }]
    })
    const payload = {
      name, notes,
      userId: props.user.id,
      geoJson,
      measurementTypeId: MEASUREMENT_TYPES.CIRCLE,
      public: false,
      measureType: 'circle'
    }
    setLoading(true)
    props.circleCreateRequest(payload)
      .then(() => { setLoading(false); props.collapseLeftToolbar() })
      .catch(err => {
        // Keep the panel open so the circle can be saved again (WEB-7164)
        console.error('Error saving circle:', err)
        setLoading(false)
        toaster.baseMapError('Couldn\'t save your circle. Please try again.')
      })
  }

  const colors = ['#EA4335', '#0000FF', '#90FF00', '#589500', '#FFFF00', '#FF6800']

  return (
    <div className='tools-content'>
      {loading && <div className='markers-load-spinner'><div className='loader-spinner'></div></div>}
      <div className='left-expanded-header-container'>
        <span className='left-expanded-header'>Add Circle</span>
        <div className='default-close left-dialog-close' onClick={() => props.collapseLeftToolbar()}></div>
      </div>

      <div>
        <label htmlFor='name' style={{ fontSize: '12px', textTransform: 'capitalize' }}>Name: </label>
        <input type='text' name='name' id='name' className='default-name-textfield'
          style={{ marginTop: '5px' }} placeholder='Name' value={name}
          onChange={e => setName(e.target.value)} />
      </div>

      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <div style={{ fontSize: '12px', marginBottom: 6 }}>CIRCLE COLOR</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {colors.map(c => (
            <div key={c}
              onClick={() => {
                setColor(c)
                colorRef.current = c
                const map = props.mapReferenceThreed
                if (map) {
                  if (map.getLayer(CIRCLE_FILL)) map.setPaintProperty(CIRCLE_FILL, 'fill-color', c)
                  if (map.getLayer(CIRCLE_LINE)) map.setPaintProperty(CIRCLE_LINE, 'line-color', c)
                }
                if (centerMarkerRef.current) {
                  const circle = centerMarkerRef.current.getElement().querySelector('circle')
                  if (circle) circle.setAttribute('fill', c)
                }
              }}
              className='color-option'
              style={{ backgroundColor: c, border: color.toUpperCase() === c.toUpperCase() ? '4px solid #c1c0c0' : '', cursor: 'pointer' }}
            >
              {color.toUpperCase() === c.toUpperCase() ? <div className='color-active'></div> : null}
            </div>
          ))}
        </div>
      </div>

      {radiusDisplay ? (
        <div style={{ fontSize: '12px', color: '#ccc', marginBottom: 12 }}>
          Radius: <strong style={{ color: '#fff' }}>{radiusDisplay}</strong>
        </div>
      ) : (
        <div style={{ fontSize: '11px', color: '#888', marginBottom: 12 }}>
          {!centerRef.current ? 'Click map to place centre' : 'Move cursor then click to set radius'}
        </div>
      )}

      <div style={{ marginTop: '10px', marginBottom: '10px' }}>
        <label htmlFor='notes' style={{ fontSize: '12px' }}>Notes:
          <textarea name='notes' id='notes' className='default-name-textfield'
            value={notes} onChange={e => setNotes(e.target.value)} placeholder=' '
            style={{ height: 100, padding: 10 }} />
        </label>
      </div>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <button style={{ width: '48%', margin: 0, backgroundColor: 'grey' }}
          onClick={() => props.collapseLeftToolbar()}>Cancel</button>
        <button style={{ width: '48%', margin: 0, backgroundColor: '#589500', cursor: 'pointer' }}
          onClick={onSave} disabled={!centerRef.current || radiusRef.current <= 0}>
          Save
        </button>
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  mapReferenceThreed: state.mapReferenceThreed,
  user: state.user,
  circles: state.circles,
})

const mapDispatchToProps = dispatch => ({
  circleCreateRequest: payload => dispatch(circleCreateRequest(payload)),
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ThreeDCircleDetails)
